import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { VideoEmbed } from "@/components/VideoEmbed";
import { useSiteContent } from "@/lib/site-content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fetches all active videos once and caches them on window for other placements.
let videosCache = null;
let videosPromise = null;

const loadVideos = async () => {
  if (videosCache) return videosCache;
  if (!videosPromise) {
    videosPromise = axios
      .get(`${API}/videos`, { params: { active_only: true } })
      .then((r) => {
        videosCache = Array.isArray(r.data) ? r.data : [];
        return videosCache;
      })
      .catch(() => {
        videosCache = [];
        return videosCache;
      });
  }
  return videosPromise;
};

/**
 * Renders every active video that was placed by the admin into the given placement slot.
 * Renders NOTHING when no videos exist for the placement (avoids empty sections).
 *
 * Props:
 *   placement: one of "startseite" | "ueber" | "coaching" | "galerie" |
 *              "referenzen" | "eigene_video_section" | "landingpage"
 *   variant:   "section" (default, adds padding + heading) | "inline" (bare embeds)
 *   title:     optional heading text (only when variant="section")
 *   eyebrow:   optional small eyebrow text above heading
 */
const VideoSection = ({
  placement,
  variant = "section",
  title,
  eyebrow,
  className = "",
}) => {
  const [videos, setVideos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { titles } = useSiteContent();

  // Only the "Über meinen Weg" section title/eyebrow is admin-editable.
  const effEyebrow = placement === "ueber" && titles.ueber_eyebrow != null ? titles.ueber_eyebrow : eyebrow;
  const effTitle = placement === "ueber" && titles.ueber_title != null ? titles.ueber_title : title;

  useEffect(() => {
    let cancelled = false;
    loadVideos().then((all) => {
      if (cancelled) return;
      const filtered = all.filter(
        (v) => v.active && v.placement === placement && v.embed_url
      );
      // sort by order asc, then created_at
      filtered.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setVideos(filtered);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [placement]);

  if (!loaded || videos.length === 0) return null;

  const grid = (
    <div
      className={`grid gap-6 ${
        videos.length === 1
          ? "grid-cols-1 max-w-3xl mx-auto"
          : videos.length === 2
          ? "grid-cols-1 md:grid-cols-2"
          : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
      }`}
    >
      {videos.map((v, i) => (
        <motion.div
          key={v.id}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: i * 0.08 }}
          className="group"
          data-testid={`video-card-${v.id}`}
        >
          <VideoEmbed video={v} />
          {(v.title || v.description) && (
            <div className="mt-3">
              {v.title && (
                <div className="font-display text-lg font-black tracking-tight text-[#F3F1E8]">
                  {v.title}
                </div>
              )}
              {v.description && (
                <p className="text-sm text-[#C9C6B4] mt-1 leading-relaxed">
                  {v.description}
                </p>
              )}
              {v.cta_text && v.cta_link && (
                <a
                  href={v.cta_link}
                  target={v.cta_link.startsWith("http") ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] text-xs font-extrabold uppercase tracking-wider rounded-full px-4 py-2"
                  data-testid={`video-cta-${v.id}`}
                >
                  {v.cta_text}
                </a>
              )}
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );

  if (variant === "inline") {
    return (
      <div className={className} data-testid={`video-section-${placement}`}>
        {grid}
      </div>
    );
  }

  return (
    <section
      id={`videos-${placement}`}
      className={`relative py-16 md:py-24 ${className}`}
      data-testid={`video-section-${placement}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(effEyebrow || effTitle) && (
          <div className="text-center mb-10 md:mb-14">
            {effEyebrow && (
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E8E5D0] mb-3">
                {effEyebrow}
              </div>
            )}
            {effTitle && (
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95] text-[#F3F1E8]">
                {effTitle}
              </h2>
            )}
          </div>
        )}
        {grid}
      </div>
    </section>
  );
};

export default VideoSection;
